/**
 * Loads the real team attached to a request (#1069 POC).
 *
 * Source of truth is `GET /requests/:id/team` via `teamInformationService`,
 * the same data the Team Information step edits. No invented personas.
 */
import { useCallback, useEffect, useState } from 'react';

import { toSpaceMembers, type SpaceMember } from './team';
import { teamInformationService } from '../../services/teamInformationService';

interface UseRequestTeam {
  members: SpaceMember[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export const useRequestTeam = (
  requestId: string | undefined,
  enabled: boolean,
): UseRequestTeam => {
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!requestId) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const team = await teamInformationService.getTeamMembers(requestId);
      setMembers(toSpaceMembers(team ?? []));
    } catch (e) {
      setMembers([]);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (!enabled) return;
    reload().catch(() => {
      // reload records the error in state; nothing to add here.
    });
  }, [enabled, reload]);

  return { members, loading, error, reload };
};
